import React, { useState, useEffect } from 'react';
import { FiCreditCard, FiAlertCircle, FiCheckCircle, FiLoader } from 'react-icons/fi';
import Modal from '../common/Modal';
import ButtonLoading from '../ButtonLoading';
import { userRequest } from '../../utils/requestMethods';

/**
 * Reusable modal for setting/editing staff bank details.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - schoolId: string
 *  - staffMember: { _id, firstname, lastname, counter_party? }
 *  - onSaved: (updatedCounterParty) => void  — callback after successful save
 */
const StaffBankDetailsModal = ({ isOpen, onClose, schoolId, staffMember, onSaved }) => {
  const [banks, setBanks] = useState([]);
  const [bankCode, setBankCode] = useState('');
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [resolving, setResolving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Pre-populate if staff already has bank details
  useEffect(() => {
    if (isOpen && staffMember?.counter_party) {
      const cp = staffMember.counter_party;
      setBankCode(cp.bank_code || '');
      setBankName(cp.bank_name || '');
      setAccountNumber(cp.account_number || '');
      setAccountName(cp.account_name || '');
    }
    if (isOpen && !staffMember?.counter_party) {
      setBankCode('');
      setBankName('');
      setAccountNumber('');
      setAccountName('');
    }
    setError('');
    setSuccess('');
  }, [isOpen, staffMember]);

  // Fetch bank list
  useEffect(() => {
    if (!isOpen) return;
    const fetchBanks = async () => {
      try {
        const { data } = await userRequest.get('/banking/withdrawal-banks');
        const result = data?.bank_codes || [];
        const seen = new Set();
        const unique = result.filter(b => {
          if (seen.has(b.bank_code)) return false;
          seen.add(b.bank_code);
          return true;
        });
        setBanks(unique);
      } catch (err) {
        console.error(err);
      }
    };
    fetchBanks();
  }, [isOpen]);

  // NIP name enquiry
  const resolveAccount = async (code, number) => {
    if (!code || !number || number.length !== 10) return;
    setResolving(true);
    setAccountName('');
    setError('');
    try {
      const { data } = await userRequest.post('banking/validate-account', {
        accounts: [{ bankCode: code, accountNumber: number }],
      });
      const result = data?.result?.[0];
      if (result?.beneficiaryName) {
        setAccountName(result.beneficiaryName);
      } else {
        setError('Could not resolve account name.');
      }
    } catch {
      setError('Error resolving account. Please try again.');
    } finally {
      setResolving(false);
    }
  };

  const handleBankSelect = (e) => {
    const code = e.target.value;
    const bank = banks.find(b => b.bank_code === code);
    setBankCode(code);
    setBankName(bank?.name || '');
    if (accountNumber.length === 10) {
      resolveAccount(code, accountNumber);
    }
  };

  const handleAccountNumberChange = (e) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 10);
    setAccountNumber(val);
  };

  const handleAccountNumberBlur = () => {
    resolveAccount(bankCode, accountNumber);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!bankCode || !bankName || !accountNumber || !accountName) {
      setError('Please complete all bank details before saving.');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { data } = await userRequest.put(`/schools/${schoolId}/finance/staff-bank-details`, {
        staff_id: staffMember._id,
        bank_code: bankCode,
        bank_name: bankName,
        account_number: accountNumber,
        account_name: accountName,
      });
      const updated = data?.data?.counter_party || { bank_code: bankCode, bank_name: bankName, account_number: accountNumber, account_name: accountName };
      setSuccess('Bank details saved successfully!');
      onSaved?.(staffMember._id, updated);
      setTimeout(() => onClose(), 1200);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to save bank details.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Bank Details — ${staffMember?.firstname || ''} ${staffMember?.lastname || ''}`}
      icon={<FiCreditCard className="text-indigo-600 text-lg" />}
      maxWidth="max-w-md"
    >
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm mb-4">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm mb-4">
          <FiCheckCircle className="mt-0.5 shrink-0" />
          <span>{success}</span>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-4">
        {/* Bank Selector */}
        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Bank</label>
          <select
            value={bankCode}
            onChange={handleBankSelect}
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          >
            <option value="">Select bank...</option>
            {banks.map(b => (
              <option key={b.bank_code} value={b.bank_code}>{b.name}</option>
            ))}
          </select>
        </div>

        {/* Account Number */}
        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Account Number</label>
          <input
            type="text"
            inputMode="numeric"
            value={accountNumber}
            onChange={handleAccountNumberChange}
            onBlur={handleAccountNumberBlur}
            placeholder="0123456789"
            maxLength={10}
            className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
          />
        </div>

        {/* Account Name (auto-resolved) */}
        <div>
          <label className="block mb-1.5 text-sm font-medium text-gray-700">Account Name</label>
          <div className="relative">
            <input
              type="text"
              value={accountName}
              readOnly
              placeholder={resolving ? 'Resolving...' : 'Auto-resolved from bank'}
              className="w-full rounded-xl bg-gray-100 border border-gray-200 px-4 py-2.5 text-sm text-gray-600 cursor-not-allowed"
            />
            {resolving && (
              <FiLoader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
            )}
          </div>
        </div>

        {/* Submit */}
        {saving ? (
          <ButtonLoading styles="w-full text-white bg-indigo-500 font-medium rounded-full text-sm px-5 py-2.5 text-center" />
        ) : (
          <button
            type="submit"
            disabled={!accountName || resolving}
            className="w-full text-white bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium rounded-full text-sm px-5 py-2.5 text-center transition-colors"
          >
            Save Bank Details
          </button>
        )}
      </form>
    </Modal>
  );
};

export default StaffBankDetailsModal;
